<!--
  Shared frame for the Reports tabs: the page title, the tab strip, and the controls each tab reads.
  A tab with a window renders the range controls on the strip's right; a tab with its own filters
  passes them through the `filters` slot, on a row beneath the strip.
-->
<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import type { ReportRangeQueryState } from '../composables/use-report-range-query-state.ts'
import ReportRangeControls from '@/features/reporting/components/ReportRangeControls.vue'
import ReportTabs from '@/features/reporting/components/ReportTabs.vue'

defineProps<{ title: string; subtitle?: string | undefined; state?: ReportRangeQueryState | undefined }>()

const { t } = useI18n()
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="title">
      <template v-if="subtitle" #subtitle>{{ subtitle }}</template>
    </PageHeader>

    <div class="flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--color-border)]">
      <ReportTabs />
      <slot name="tabs-end">
        <ReportRangeControls v-if="state" :state="state" />
      </slot>
    </div>

    <slot name="filters" />

    <p v-if="state?.isInverted.value" role="alert" class="text-[color:var(--color-attention)]">
      {{ t('reporting.invertedRange') }}
    </p>
    <p v-else-if="state?.isTooLong.value" role="alert" class="text-[color:var(--color-attention)]">
      {{ t('reporting.rangeTooLong') }}
    </p>

    <slot />
  </PageShell>
</template>
